import React, { useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { RESET_ONBOARDING } from '../../redux/types'
import { STORIES } from 'routes/routeConstant'
import {
  claimOnboardingDayPass,
  getStripePlan,
  stripeCheckoutLink,
  validateCouponCode,
} from '../../redux/actions/myAccountAction'
import { membershipNameType } from '../../utils/constant'
import { RegionalPricingContext, formatMoney, getPlanAmount } from 'utils/currency'
import RegionalPricingNotice from '../../containers/regional-pricing/RegionalPricingNotice'
import ExpressCheckoutRow from '../../containers/express-checkout/ExpressCheckoutRow'
import { checkOtpAuth } from 'utils/CheckAuthentication'
import {
  OfferCampaign,
  campaignAppliesToProduct,
  clearPartnerCode,
  getOfferPricing,
  getPendingPartnerCode,
  getStoredOfferCampaign,
  resolvePartnerValidation,
  storePartnerCampaign,
} from 'utils/offerCoupon'
import { hapticSuccess } from 'utils/haptics'
import posthog from 'posthog-js'
import CorrectionDemo from './CorrectionDemo'
import { useTrialDays } from '../referral/useTrialDays'
import './trial-offer.css'
import { ONBOARDING_PERSIST_STORAGE_KEY } from '../../redux/store'
import { planFeatureLabel } from '../../utils/planFeatureLabel'
import { STUDENT_DISCOUNT_FAQ_KEYS, requestStudentDiscount } from '../../utils/studentDiscount'
import { formatNumber, formatPercent } from '../../i18n/format'

// All trials are capped at 5 days: ad conversions fire on the first payment
// (7-day attribution window), and a short trial means the level and known
// words set during onboarding are still accurate when the trial ends.
// The one exception is a friend invited through the give-get referral program,
// who gets 14 days (see useTrialDays / dev-backend/src/core/referral).
// No-card Standard trial granted when a user declines the card trial
const DAY_PASS_DAYS = 5

const CHECKOUT_ERROR_KEY = 'onboarding:trialOffer.checkoutError'

/**
 * Event properties stay in English whatever the interface language, so analytics keep one value
 * per question or session. English is the fallback language, so its strings are always loaded.
 */
const englishT = (key: string, values?: Record<string, unknown>) => i18n.getFixedT('en')(key, values) as string

/**
 * Silent, ambient video — autoplays muted and loops with no controls. The
 * 1-on-1 feedback-session clip has no audio track, so an ambient loop reads as
 * a quiet window into a real session (and needs no play chrome).
 */
const AmbientVideo = ({ src, caption }: { src: string; caption: string }) => (
  <figure className='trial-offer-ambient'>
    <video className='trial-offer-ambient-media' src={src} autoPlay loop muted playsInline preload='auto' />
    <figcaption className='trial-offer-ambient-caption'>{caption}</figcaption>
  </figure>
)

/**
 * What actually happens across the free days (5, or 14 for a referred friend). Deliberately makes no promise of a
 * "your trial is ending" reminder email: the backend has no `customer.subscription
 * .trial_will_end` handler and no reminder job, so claiming one would be a promise
 * nothing keeps. If that mail is ever built, add the step back here.
 */
const TRIAL_TIMELINE = [
  {
    id: 'today',
    when: 'onboarding:trialOffer.timeline.today.when',
    title: 'onboarding:trialOffer.timeline.today.title',
    body: 'onboarding:trialOffer.timeline.today.body',
  },
  {
    id: 'habit',
    when: 'onboarding:trialOffer.timeline.habit.when',
    title: 'onboarding:trialOffer.timeline.habit.title',
    body: 'onboarding:trialOffer.timeline.habit.body',
  },
  {
    id: 'end',
    when: 'onboarding:trialOffer.timeline.end.when',
    title: 'onboarding:trialOffer.timeline.end.title',
    body: 'onboarding:trialOffer.timeline.end.body',
  },
]

/**
 * Objection-handling FAQ. Every answer is kept to things the product actually does:
 * plan switching and cancelling both run through Settings → Plan (see the churn flow
 * in components/setting/plan-tab), and the level comes from the placement test the
 * user has just finished. Each entry holds translation keys; `days` fills them in.
 */
const TRIAL_FAQ: { q: string; a: string; action?: string }[] = [
  {
    q: 'onboarding:trialOffer.faq.difference.question',
    a: 'onboarding:trialOffer.faq.difference.answer',
  },
  {
    q: 'onboarding:trialOffer.faq.trialEnd.question',
    a: 'onboarding:trialOffer.faq.trialEnd.answer',
  },
  {
    q: 'onboarding:trialOffer.faq.cancel.question',
    a: 'onboarding:trialOffer.faq.cancel.answer',
  },
  {
    q: 'onboarding:trialOffer.faq.beginner.question',
    a: 'onboarding:trialOffer.faq.beginner.answer',
  },
  {
    q: 'onboarding:trialOffer.faq.teachers.question',
    a: 'onboarding:trialOffer.faq.teachers.answer',
  },
  {
    q: 'onboarding:trialOffer.faq.missDay.question',
    a: 'onboarding:trialOffer.faq.missDay.answer',
  },
  { q: STUDENT_DISCOUNT_FAQ_KEYS.q, a: STUDENT_DISCOUNT_FAQ_KEYS.a, action: STUDENT_DISCOUNT_FAQ_KEYS.action },
  {
    q: 'onboarding:trialOffer.faq.switchPlans.question',
    a: 'onboarding:trialOffer.faq.switchPlans.answer',
  },
]

/**
 * Verified against maayotProd on 2026-08-28. Do not round these up.
 *
 *  learners  `users`, 66,439 rows less the 400 matching @maayot.com / test /
 *            example.com, i.e. 66,039 real accounts. Shown as 65,000+ so the
 *            claim stays true as the collection grows rather than going stale.
 *  stories   `language_story`, a straight count of 5,886.
 *  correct.  `airbyte_raw_story_correction`, a straight count of 4,914.
 *
 * These are registered accounts, not active subscribers — the same basis as
 * Duolingo's "500m+ learners". Don't let the label drift to "active users".
 */
const PROOF_STATS = [
  { figure: 65000, label: 'onboarding:trialOffer.stats.learners' },
  { figure: 5800, label: 'onboarding:trialOffer.stats.lessons' },
  { figure: 4900, label: 'onboarding:trialOffer.stats.corrections' },
]

/**
 * Real reviews, quoted verbatim from the public store listings and one review
 * site. Names and sources are shown exactly as they appear there — the source
 * line is the proof, so it must never be dropped for visual tidiness.
 *
 * Deliberately no aggregate rating anywhere on this page: the App Store average
 * is 3.9 from 50 ratings, which would undercut the very quotes it sat beside.
 *
 * Not translated: a quote stays in the words the reviewer wrote.
 */
const LEARNER_REVIEWS = [
  {
    quote:
      'I am obsessed with this app. Pro is truly worth it. Daily stories with the right amount of challenge… has taken my reading to the next level.',
    name: 'V Sethu',
    source: 'App Store · Oct 2025',
  },
  {
    quote:
      'I really like the daily stories and how they reinforce new vocabulary by having it appear across several days!',
    name: 'Jeannie Fu',
    source: 'Google Play',
  },
  {
    quote: 'I love the readings and the ability to click and see pinyin if needed.',
    name: 'Chewy7000',
    source: 'App Store',
  },
  {
    quote: 'maayot helps to motivate you by delivering daily bite-sized reading activities to your inbox.',
    name: 'Tara',
    source: 'All Language Resources',
  },
]

const STARS = '★★★★★'

const BILLING_OPTIONS = ['monthly', 'yearly'] as const

const FiveStars = ({ className = 'trial-offer-stars' }: { className?: string }) => {
  const { t } = useTranslation('onboarding')
  return (
    <span className={className} role='img' aria-label={t('trialOffer.reviews.rated')}>
      {STARS}
    </span>
  )
}

/**
 * The two 1-on-1 sessions that come with Pro, told as a cadence rather than a
 * list: one at the start, one recurring every quarter. Copy is carried over
 * from the live /setting#plan page, which is the source of both claims.
 *
 * Note for whoever maintains this: the welcome call is fully built (a Trafft
 * booking embed on the post-payment screen plus native sheets in both apps),
 * whereas the quarterly review has no scheduler anywhere in the codebase and
 * is run manually. Both are promised on the live plan page today.
 */
const SESSIONS = [
  {
    tab: 'onboarding:trialOffer.sessions.welcome.tab',
    cadence: 'onboarding:trialOffer.sessions.welcome.cadence',
    minutes: 20,
    title: 'onboarding:trialOffer.sessions.welcome.title',
    body: 'onboarding:trialOffer.sessions.welcome.body',
    points: [
      'onboarding:trialOffer.sessions.welcome.pointLevel',
      'onboarding:trialOffer.sessions.welcome.pointFocus',
    ],
  },
  {
    tab: 'onboarding:trialOffer.sessions.review.tab',
    cadence: 'onboarding:trialOffer.sessions.review.cadence',
    minutes: 40,
    title: 'onboarding:trialOffer.sessions.review.title',
    body: 'onboarding:trialOffer.sessions.review.body',
    points: [
      'onboarding:trialOffer.sessions.review.pointPatterns',
      'onboarding:trialOffer.sessions.review.pointGuidance',
      'onboarding:trialOffer.sessions.review.pointObjectives',
      'onboarding:trialOffer.sessions.review.pointLessons',
    ],
  },
]

/**
 * Two sessions as a switcher rather than two stacked cards. Stacked, they read
 * as a feature list; switched, the rail across the top reads as a calendar and
 * the section makes its real point, which is that this is an ongoing
 * relationship and not a one-off onboarding call.
 */
const SessionSwitcher = ({ onPick }: { onPick: (title: string) => void }) => {
  const { t } = useTranslation('onboarding')
  const [index, setIndex] = useState(0)
  const session = SESSIONS[index]

  return (
    <div className='trial-offer-sessions'>
      <div className='trial-offer-session-rail' role='tablist' aria-label={t('trialOffer.sessions.railLabel')}>
        {SESSIONS.map((item, i) => (
          <button
            key={item.title}
            type='button'
            role='tab'
            aria-selected={i === index}
            className={`trial-offer-session-tab${i === index ? ' is-active' : ''}`}
            onClick={() => {
              setIndex(i)
              onPick(englishT(item.title))
            }}
          >
            <span className='trial-offer-session-tab-when'>{t(item.cadence)}</span>
            <span className='trial-offer-session-tab-name'>{t(item.title)}</span>
          </button>
        ))}
      </div>

      <article className='trial-offer-session' key={session.title}>
        <div className='trial-offer-session-figure'>
          <span className='trial-offer-session-num'>{formatNumber(session.minutes)}</span>
          <span className='trial-offer-session-unit'>
            {t('trialOffer.sessions.minutes', { count: session.minutes })}
          </span>
        </div>
        <div className='trial-offer-session-body'>
          <span className='trial-offer-session-kicker'>{t(session.tab)}</span>
          <h3 className='trial-offer-session-title'>{t(session.title)}</h3>
          <p>{t(session.body)}</p>
          <ul className='trial-offer-session-list'>
            {session.points.map((point) => (
              <li key={point}>{t(point)}</li>
            ))}
          </ul>
        </div>
      </article>
    </div>
  )
}

/**
 * Exit offer: a pop-up shown once per browser session when a visitor looks about to leave
 * without choosing a plan. Its button starts a Pro monthly card trial. It only appears after
 * the visitor has been on the page for EXIT_OFFER_MIN_MS.
 */
const EXIT_OFFER_SEEN_KEY = 'maayot_exit_offer_seen'
const EXIT_OFFER_MIN_MS = 6000

/**
 * "All in one app" feature list. Every line maps to something the product does today
 * (stories at the learner's level, audio, quiz, saved words, teacher corrections). The
 * two correction rows are Pro only, so they carry the Pro badge.
 */
const ALL_IN_ONE: { icon: React.ReactNode; name: string; desc: string; pro?: boolean }[] = [
  {
    icon: (
      <>
        <rect x='3' y='4' width='8' height='16' rx='1.5' />
        <rect x='13' y='4' width='8' height='16' rx='1.5' />
        <path d='M6 8h2M6 11h2M6 14h2' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.stories.name',
    desc: 'onboarding:trialOffer.allInOne.stories.desc',
  },
  {
    icon: (
      <>
        <path d='M4 15v-3a8 8 0 0 1 16 0v3' />
        <rect x='3' y='14' width='4' height='6' rx='2' />
        <rect x='17' y='14' width='4' height='6' rx='2' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.audio.name',
    desc: 'onboarding:trialOffer.allInOne.audio.desc',
  },
  {
    icon: (
      <>
        <circle cx='12' cy='12' r='9' />
        <path d='M9.6 9.5a2.5 2.5 0 1 1 3.6 2.2c-.8.4-1.2 1-1.2 1.8' />
        <path d='M12 17h.01' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.quizzes.name',
    desc: 'onboarding:trialOffer.allInOne.quizzes.desc',
  },
  {
    icon: (
      <>
        <path d='M4 20h16' />
        <path d='M7 16v-3M12 16V9M17 16V5' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.mastery.name',
    desc: 'onboarding:trialOffer.allInOne.mastery.desc',
  },
  {
    icon: (
      <>
        <path d='M4 20h16' />
        <path d='M6 16l1-4 8.5-8.5a1.8 1.8 0 0 1 2.5 2.5L9.5 14.5 6 16z' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.writing.name',
    desc: 'onboarding:trialOffer.allInOne.writing.desc',
    pro: true,
  },
  {
    icon: (
      <>
        <rect x='9' y='3' width='6' height='11' rx='3' />
        <path d='M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6' />
      </>
    ),
    name: 'onboarding:trialOffer.allInOne.speaking.name',
    desc: 'onboarding:trialOffer.allInOne.speaking.desc',
    pro: true,
  },
]

const AllInOne = () => {
  const { t } = useTranslation('onboarding')
  return (
    <div className='trial-offer-allin'>
      <h2 className='trial-offer-allin-title'>
        <Trans t={t} i18nKey='trialOffer.allInOne.title' components={{ span: <span /> }} />
      </h2>
      <ul className='trial-offer-allin-grid'>
        {ALL_IN_ONE.map((item) => (
          <li className='trial-offer-allin-item' key={item.name}>
            <svg className='trial-offer-allin-icon' viewBox='0 0 24 24' aria-hidden='true'>
              {item.icon}
            </svg>
            <div>
              <div className='trial-offer-allin-name'>
                {t(item.name)}
                {item.pro && <span className='trial-offer-allin-pro'>{t('trialOffer.allInOne.proBadge')}</span>}
              </div>
              <p className='trial-offer-allin-desc'>{t(item.desc)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Keyboard and screen-reader basics for a modal dialog: focus moves into it when it opens,
 * Escape closes it, and focus goes back to where it was when it closes.
 */
const useDialogFocus = (open: boolean, onClose: () => void) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open])
  return dialogRef
}

/** The free trial, as a three-beat timeline. Pure presentation. */
const TrialTimeline = ({ days }: { days: number }) => {
  const { t } = useTranslation('onboarding')
  const values = { day: days, lastDay: days - 1 }
  return (
    <ol className='trial-offer-timeline'>
      {TRIAL_TIMELINE.map((step) => (
        <li className='trial-offer-timeline-step' key={step.id}>
          <span className='trial-offer-timeline-when'>{t(step.when, values)}</span>
          <div className='trial-offer-timeline-body'>
            <h3 className='trial-offer-timeline-title'>{t(step.title)}</h3>
            <p>
              <Trans t={t} i18nKey={step.body} values={values} components={{ strong: <strong /> }} />
            </p>
          </div>
        </li>
      ))}
    </ol>
  )
}

/**
 * Single-open accordion. Native <button> + aria-expanded so it is keyboard and
 * screen-reader operable; the panel is unmounted rather than hidden so collapsed
 * answers never get picked up by in-page search or the accessibility tree.
 */
const FaqAccordion = ({
  onOpen,
  onAction,
  days,
}: {
  onOpen: (question: string) => void
  /** The one answer with a button (the student and teacher discount) calls this. */
  onAction: () => void
  days: number
}) => {
  const { t } = useTranslation(['onboarding', 'offers'])
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <ul className='trial-offer-faq'>
      {TRIAL_FAQ.map((item, i) => {
        const isOpen = openIndex === i
        return (
          <li className={`trial-offer-faq-item${isOpen ? ' is-open' : ''}`} key={item.q}>
            <button
              type='button'
              className='trial-offer-faq-q'
              aria-expanded={isOpen}
              onClick={() => {
                const next = isOpen ? null : i
                setOpenIndex(next)
                if (next !== null) onOpen(englishT(item.q, { days }))
              }}
            >
              <span>{t(item.q, { days })}</span>
              <span className='trial-offer-faq-chev' aria-hidden='true' />
            </button>
            {isOpen && (
              <div className='trial-offer-faq-a'>
                {t(item.a, { days })}
                {item.action && (
                  <button type='button' className='trial-offer-faq-action' onClick={onAction}>
                    {t(item.action)}
                  </button>
                )}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TrialOfferRedesigned = ({ next }: { next: () => void }) => {
  const { t } = useTranslation(['onboarding', 'offers'])
  const dispatch = AppUseDispatch()
  const navigate = useNavigate()
  const { membership, credentials } = AppUseSelector(({ user }) => user)
  // 5 days, or 14 once the server confirms this learner came through a friend's referral link.
  const trialDays = useTrialDays()
  const PRO_TRIAL_DAYS = trialDays
  const STANDARD_TRIAL_DAYS = trialDays
  // A campaign coupon picked up on /offer, or a partner code from a /signup?code= link,
  // several screens back. A campaign is only ever set while its window is open. A partner
  // code starts unverified and is checked against Stripe below before any discount shows.
  const [offerCampaign, setOfferCampaign] = useState<OfferCampaign | undefined>(() => getStoredOfferCampaign())
  const pendingPartnerCode = useMemo(() => (offerCampaign ? undefined : getPendingPartnerCode()), [])
  // The ad (or the partner) promised a monthly discount, so open on monthly. Landing these
  // arrivals on the yearly tab showed them a full yearly price and silently dropped the
  // coupon, which is only attached to monthly checkouts.
  const [billing, setBilling] = useState<'monthly' | 'yearly'>(
    offerCampaign || pendingPartnerCode ? 'monthly' : 'yearly',
  )
  const [stripePlan, setStripePlan] = useState<any>(null)
  const [regionalPricing, setRegionalPricing] = useState<RegionalPricingContext | null>(null)
  const [processingPlan, setProcessingPlan] = useState<string | null>(null)
  const [showDayPassModal, setShowDayPassModal] = useState(false)
  // Opting out is a real decision, so it gets a confirmation that spells out what
  // is being given up rather than silently dropping the user onto Standard.
  const [showSkipConfirm, setShowSkipConfirm] = useState(false)
  const [showExitOffer, setShowExitOffer] = useState(false)
  const [isClaimingDayPass, setIsClaimingDayPass] = useState(false)
  const [dayPassGranted, setDayPassGranted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const plansRef = useRef<HTMLElement>(null)
  // The plan cards sit several screens below the fold, so a sticky bar carries the CTA
  // until they are actually on screen — then it gets out of the way of the real buttons.
  const [showStickyCta, setShowStickyCta] = useState(false)

  const isPaidMember = Boolean(membership?.name) && membership?.name !== membershipNameType.Free

  // GTM payload kept byte-identical to what ad wiring expects; PostHog mirror added
  const track = (event: string, extra: Record<string, any> = {}) => {
    window.dataLayer?.push({
      event,
      memberId: credentials?.memberId,
      offer_variant: 'redesign',
      ...extra,
      page: {
        url: window.location.href,
        title: 'maayot • Your daily Chinese lesson',
      },
    })
    try {
      posthog.capture(event, { memberId: credentials?.memberId, offer_variant: 'redesign', ...extra })
    } catch {
      /* analytics must never break the funnel */
    }
  }

  // Leave onboarding for the dashboard, clearing onboarding state the same way WordResult did
  // before this step existed. Pass refresh=true to pull a fresh profile first (e.g. after the day
  // pass grants a Standard trial) so /stories reflects the new plan instead of stale Free state.
  const finishOnboarding = async (refresh = false) => {
    if (refresh) {
      try {
        await checkOtpAuth()
      } catch {
        /* even if the refresh fails, don't trap the user in onboarding */
      }
    }
    // Navigate BEFORE resetting: clearing the step first re-renders the
    // orchestrator on its first step, flashing the "why are you learning"
    // screen while the profile refresh is in flight.
    navigate(STORIES)
    localStorage.removeItem(ONBOARDING_PERSIST_STORAGE_KEY)
    dispatch({ type: RESET_ONBOARDING })
  }

  useEffect(() => {
    // Paid members (e.g. subscribers re-running placement via the /existing flow) never see the offer
    if (isPaidMember) {
      finishOnboarding()
      return
    }
    track('Onboarding Trial Offer Viewed')
    getStripePlan(credentials?.memberId).then((res: any) => {
      if (res?.monthly || res?.yearly) {
        setStripePlan(res)
        setRegionalPricing(res?.pricing || null)
      }
    })
  }, [isPaidMember])

  // Sticky CTA is shown only while the plan cards are off screen. Guarded because
  // jsdom in the unit tests and older Safari have no IntersectionObserver — without a
  // guard the whole step would fail to mount rather than just skipping the bar.
  useEffect(() => {
    const node = plansRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setShowStickyCta(!entry.isIntersecting), {
      rootMargin: '-80px 0px 0px 0px',
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [stripePlan])

  // Exit offer. Desktop: the pointer leaves through the top of the window (towards the tab bar).
  // Phones have no pointer, so the signal is the visitor switching away and coming back. It shows
  // once per visit, only after a few seconds on the page, and never over a choice already in progress.
  const exitBlockedRef = useRef(false)
  exitBlockedRef.current = Boolean(processingPlan) || showSkipConfirm || showDayPassModal || showExitOffer
  useEffect(() => {
    if (isPaidMember) return
    let alreadySeen = false
    try {
      alreadySeen = sessionStorage.getItem(EXIT_OFFER_SEEN_KEY) === '1'
    } catch {
      /* private mode: fall back to once per page load */
    }
    if (alreadySeen) return
    const openedAt = Date.now()
    let fired = false
    const fire = (trigger: string) => {
      if (fired || exitBlockedRef.current || Date.now() - openedAt < EXIT_OFFER_MIN_MS) return
      fired = true
      try {
        sessionStorage.setItem(EXIT_OFFER_SEEN_KEY, '1')
      } catch {
        /* ignore */
      }
      track('Onboarding Exit Offer Shown', { trigger })
      setShowExitOffer(true)
    }
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) fire('mouse_exit')
    }
    let wentAway = false
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wentAway = true
        return
      }
      // Each return is judged on its own: a visit that was too early, or blocked by an open
      // dialog or checkout, must not make the next return fire straight away.
      const returning = wentAway
      wentAway = false
      if (returning) fire('tab_return')
    }
    document.addEventListener('mouseout', onMouseOut)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('mouseout', onMouseOut)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isPaidMember])

  const skipDialogRef = useDialogFocus(showSkipConfirm, () => handle.dismissSkip('close'))
  const exitDialogRef = useDialogFocus(showExitOffer, () => {
    if (!processingPlan) handle.closeExitOffer('close')
  })

  const getPlan = (interval: 'monthly' | 'yearly', value: string) =>
    (stripePlan?.[interval] || []).find((p: any) => p?.value === value)

  // Verify a partner code once the prices are in (validate-coupon wants a price id and a
  // login, both of which exist by this step). A code Stripe does not accept is dropped
  // quietly: the learner sees list prices and checkout never receives it.
  useEffect(() => {
    if (!pendingPartnerCode || offerCampaign || !stripePlan || isPaidMember) return
    const monthly = stripePlan?.monthly || []
    const priceId = (monthly.find((p: any) => p?.priceId) || {}).priceId
    if (!priceId) return
    let cancelled = false
    validateCouponCode({ code: pendingPartnerCode, priceId }).then((res: any) => {
      if (cancelled) return
      const outcome = resolvePartnerValidation(pendingPartnerCode, res)
      if (outcome.status === 'applied') {
        const campaign = outcome.campaign
        storePartnerCampaign(campaign)
        setOfferCampaign(campaign)
        track('Onboarding Partner Code Applied', {
          partnerCode: campaign.code,
          discountPercent: campaign.discountPercent,
          durationMonths: campaign.durationMonths,
        })
      } else if (outcome.status === 'rejected') {
        clearPartnerCode()
        track('Onboarding Partner Code Rejected', {
          partnerCode: pendingPartnerCode,
          reason: outcome.reason,
        })
      } else {
        // The check never got an answer (offline, timeout, 5xx). The code is kept so the
        // next load can re-check it — throwing it away here would cost the partner a sale
        // over one bad request. List prices show in the meantime, so this still fails closed.
        track('Onboarding Partner Code Check Unavailable', { partnerCode: pendingPartnerCode })
      }
    })
    return () => {
      cancelled = true
    }
  }, [pendingPartnerCode, stripePlan, isPaidMember])

  // The coupon only goes on monthly checkouts, and only for plans its Stripe coupon covers.
  const couponFor = (plan: any, interval: 'monthly' | 'yearly' = billing) =>
    interval === 'monthly' && campaignAppliesToProduct(offerCampaign, plan?.productId) ? offerCampaign?.code : undefined

  // price on yearly plans is the annual charge; cards display the monthly equivalent
  const buildCardData = (value: string) => {
    const plan = getPlan(billing, value)
    if (!plan) return null
    const listPrice = getPlanAmount(plan)
    // The campaign coupon is monthly-only (its Stripe coupon is duration_in_months), so the
    // yearly tab stays at list price and says nothing about a discount it cannot honour.
    const offer = couponFor(plan) ? getOfferPricing(listPrice, undefined, offerCampaign || null) : null
    return {
      ...plan,
      monthlyEquivalent: offer ? offer.promoMonthly : billing === 'yearly' ? listPrice / 12 : listPrice,
      regularMonthly: offer ? offer.regularMonthly : undefined,
      offerDurationMonths: offer ? offer.campaign.durationMonths : undefined,
      yearlyCharge: billing === 'yearly' ? listPrice : undefined,
    }
  }

  const proPlan = useMemo(() => buildCardData(membershipNameType.Pro), [stripePlan, billing, offerCampaign])
  const standardPlan = useMemo(() => buildCardData(membershipNameType.Standard), [stripePlan, billing, offerCampaign])

  // Day-pass modal anchors on the real monthly list price ($15/month), not the
  // yearly-equivalent the billing toggle happens to be on.
  const standardMonthlyDisplayPrice = useMemo(() => {
    const plan = getPlan('monthly', membershipNameType.Standard)
    const price = plan ? getPlanAmount(plan) : 0
    // Drop a ".00" (or ",00") so a whole price reads "$15", wherever the locale puts the symbol.
    return price > 0 ? formatMoney(price, plan?.currency).replace(/[.,]00(?=\D*$)/, '') : null
  }, [stripePlan])

  const yearlySavePercent = useMemo(() => {
    const monthly = getPlan('monthly', membershipNameType.Pro)
    const yearly = getPlan('yearly', membershipNameType.Pro)
    if (!monthly || !yearly) return 0
    const monthlyPrice = getPlanAmount(monthly)
    const yearlyPrice = getPlanAmount(yearly)
    if (!monthlyPrice || !yearlyPrice) return 0
    return Math.ceil(((monthlyPrice - yearlyPrice / 12) / monthlyPrice) * 100)
  }, [stripePlan])

  const trialDaysFor = (plan: any) => (plan?.value === membershipNameType.Pro ? PRO_TRIAL_DAYS : STANDARD_TRIAL_DAYS)

  const handle = {
    // Hero and sticky-bar CTAs don't start checkout directly — the user still has to pick
    // a plan — so they carry the reader to the cards instead.
    scrollToPlans: (source: string) => {
      track('Onboarding Trial Plans Viewed From CTA', { source })
      plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    faqOpened: (question: string) => track('Onboarding Trial FAQ Opened', { question }),
    sessionPicked: (session: string) => track('Onboarding Trial Session Viewed', { session }),
    confirmSkip: () => {
      track('Onboarding Trial Skip Confirm Shown')
      setShowSkipConfirm(true)
    },
    dismissSkip: (choice: 'plans' | 'close') => {
      track('Onboarding Trial Skip Reconsidered', { choice })
      setShowSkipConfirm(false)
      if (choice === 'plans') plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // "Start free" in the exit pop-up goes straight to Stripe Checkout with Pro MONTHLY (the plan the
    // pop-up describes, and it never shows a price, so it must not default to a yearly charge). The
    // pop-up stays up, showing "Starting your trial…", until the redirect. If checkout can't start, it
    // closes and drops the visitor on the plan cards, where the error message shows.
    closeExitOffer: async (choice: 'trial' | 'close'): Promise<void> => {
      track('Onboarding Exit Offer Closed', { choice })
      const monthlyPro = getPlan('monthly', membershipNameType.Pro)
      if (choice === 'trial' && monthlyPro) {
        const started = await handle.startTrial(monthlyPro, 'exit_offer', 'monthly')
        if (started) return
      }
      setShowExitOffer(false)
      if (choice === 'trial') plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    startTrial: async (plan: any, source?: string, interval: 'monthly' | 'yearly' = billing): Promise<boolean> => {
      if (!plan || processingPlan) return false
      setErrorMessage('')
      setProcessingPlan(plan.value)
      track('Onboarding Trial Checkout Started', {
        planName: plan.value,
        billing: interval,
        trialDays: trialDaysFor(plan),
        ...(source && { source }),
      })

      const affiliateId =
        typeof window !== 'undefined' && (window as any).affiliateId ? String((window as any).affiliateId) : undefined

      // Monthly only: the coupon is duration_in_months and the server rejects it against a
      // yearly price, which would drop it and charge the list price after showing a discount.
      const offerCoupon = couponFor(plan, interval)

      try {
        const res: any = await stripeCheckoutLink({
          priceId: plan.priceId,
          trialDays: trialDaysFor(plan),
          ...(plan.currency && plan.currency !== 'USD' && { currency: plan.currency }),
          ...(affiliateId && { affiliateId }),
          ...(offerCoupon && { couponCode: offerCoupon }),
        })
        const checkoutUrl = res?.return_url || res?.url
        if (checkoutUrl) {
          // Onboarding is done — drop the persisted state before leaving for Stripe
          localStorage.removeItem(ONBOARDING_PERSIST_STORAGE_KEY)
          window.open(checkoutUrl, '_self')
          return true
        }
        setErrorMessage(CHECKOUT_ERROR_KEY)
        setProcessingPlan(null)
      } catch (e) {
        setErrorMessage(CHECKOUT_ERROR_KEY)
        setProcessingPlan(null)
      }
      return false
    },
    // "Explore on my own" no longer opens a decision modal. We immediately activate the user's
    // 5-day, no-card Standard trial (a real Stripe subscription that auto-cancels) and show a
    // confirmation message so they understand this is normally a paid plan we're granting for free.
    grantDayPass: async () => {
      if (isClaimingDayPass || dayPassGranted) return
      track('Onboarding Trial Skipped')
      setShowSkipConfirm(false)
      setShowDayPassModal(true)
      setIsClaimingDayPass(true)
      let granted = false
      try {
        const res: any = await claimOnboardingDayPass()
        if (res?.granted) {
          granted = true
          hapticSuccess()
          track('Onboarding Day Pass Claimed')
        }
      } catch (e) {
        // Whatever happened, don't trap the user in onboarding — they land on /stories either way
      }
      // Track grant outcome so "Start today’s lesson" refreshes the profile only when the trial was
      // actually granted, so /stories reflects the new Standard plan instead of stale Free state.
      setDayPassGranted(granted)
      setIsClaimingDayPass(false)
    },
    continueAfterDayPass: () => {
      finishOnboarding(dayPassGranted)
    },
  }

  if (isPaidMember) return null

  const proDays = { days: PRO_TRIAL_DAYS }
  const proCtaLabel =
    processingPlan === proPlan?.value ? t('trialOffer.startingTrial') : t('trialOffer.tryProFree', proDays)

  return (
    <div className='trial-offer'>
      {/* ---------------------------------------------------------------
          Header — leads with the human differentiator, then the promise
          --------------------------------------------------------------- */}
      <header className='trial-offer-header'>
        <span className='trial-offer-eyebrow'>{t('trialOffer.header.eyebrow')}</span>
        <h1 className='trial-offer-title'>
          <Trans
            t={t}
            i18nKey='trialOffer.header.title'
            components={{ em: <span className='trial-offer-title-em' /> }}
          />
        </h1>
        <p className='trial-offer-subtitle'>{t('trialOffer.header.subtitle', proDays)}</p>
        <button type='button' className='trial-offer-hero-cta' onClick={() => handle.scrollToPlans('hero')}>
          {t('trialOffer.header.cta', proDays)}
        </button>
        <p className='trial-offer-hero-note'>{t('trialOffer.noChargeCancelAnyTime')}</p>
      </header>

      {/* Verified product numbers — see PROOF_STATS for the source of each. */}
      <ul className='trial-offer-stats'>
        {PROOF_STATS.map((stat) => (
          <li className='trial-offer-stat' key={stat.label}>
            <span className='trial-offer-stat-figure'>{`${formatNumber(stat.figure)}+`}</span>
            <span className='trial-offer-stat-label'>{t(stat.label)}</span>
          </li>
        ))}
      </ul>

      <section className='trial-offer-block trial-offer-block--plans' id='plans' ref={plansRef}>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker'>{t('trialOffer.plans.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.plans.title', proDays)}</h2>
          <p className='trial-offer-block-lead'>{t('trialOffer.plans.lead')}</p>
        </div>

        <RegionalPricingNotice pricing={regionalPricing} />

        <div className='trial-offer-billing'>
          <div className='trial-offer-billing-group' role='tablist'>
            {BILLING_OPTIONS.map((value) => (
              <button
                key={value}
                type='button'
                role='tab'
                aria-selected={billing === value}
                className={`trial-offer-billing-btn${billing === value ? ' active' : ''}`}
                onClick={() => setBilling(value)}
              >
                {value === 'monthly' ? t('trialOffer.plans.monthly') : t('trialOffer.plans.yearly')}
                {value === 'yearly' && yearlySavePercent > 0 && (
                  <span className='trial-offer-billing-save'>
                    {t('trialOffer.plans.save', { percent: formatPercent(yearlySavePercent / 100) })}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {!stripePlan ? (
          <div className='trial-offer-loader'>
            <Spinner animation='border' />
          </div>
        ) : (
          <div className='trial-offer-cards'>
            {proPlan && (
              <div className='trial-offer-card trial-offer-card--pro'>
                <h3 className='trial-offer-card-name'>{t('trialOffer.plans.pro.name')}</h3>
                <p className='trial-offer-card-tagline'>{t('trialOffer.plans.pro.tagline')}</p>
                <div className='trial-offer-card-price'>
                  {proPlan.regularMonthly !== undefined && (
                    <span className='trial-offer-card-amount-was'>
                      {formatMoney(proPlan.regularMonthly, proPlan.currency)}
                    </span>
                  )}
                  <span className='trial-offer-card-amount'>
                    {formatMoney(proPlan.monthlyEquivalent, proPlan.currency)}
                  </span>
                  <span className='trial-offer-card-period'>{t('trialOffer.plans.perMonth')}</span>
                </div>
                {proPlan.regularMonthly !== undefined && (
                  <div className='trial-offer-card-billing-note'>
                    {t('trialOffer.plans.offerNote', {
                      months: proPlan.offerDurationMonths,
                      price: formatMoney(proPlan.regularMonthly, proPlan.currency),
                    })}
                  </div>
                )}
                {proPlan.yearlyCharge !== undefined && (
                  <div className='trial-offer-card-billing-note'>
                    {t('trialOffer.plans.yearlyNote', { price: formatMoney(proPlan.yearlyCharge, proPlan.currency) })}
                  </div>
                )}
                <ExpressCheckoutRow
                  priceId={proPlan.priceId}
                  currency={proPlan.currency}
                  couponCode={couponFor(proPlan)}
                  trialDays={trialDaysFor(proPlan)}
                  onConfirmStart={() => {
                    track('Onboarding Trial Checkout Started', {
                      planName: proPlan.value,
                      billing,
                      trialDays: trialDaysFor(proPlan),
                      method: 'wallet',
                    })
                    // Same hand-off as the button path: onboarding is done, drop the persisted
                    // state before the wallet confirmation navigates away.
                    localStorage.removeItem(ONBOARDING_PERSIST_STORAGE_KEY)
                  }}
                />
                <button
                  type='button'
                  className='trial-offer-cta trial-offer-cta--pro'
                  onClick={() => handle.startTrial(proPlan)}
                  disabled={Boolean(processingPlan)}
                >
                  {proCtaLabel}
                </button>
                <ul className='trial-offer-feats'>
                  <li className='trial-offer-feat trial-offer-feat--kicker'>
                    {t('trialOffer.plans.pro.featuresIntro')}
                  </li>
                  <li className='trial-offer-feat'>
                    <span className='trial-offer-feat-lead'>{t('trialOffer.plans.pro.writingLead')}</span>
                    <span className='trial-offer-feat-rest'>
                      <Trans t={t} i18nKey='trialOffer.plans.pro.writingRest' components={{ em: <em /> }} />
                    </span>
                  </li>
                  <li className='trial-offer-feat'>
                    <span className='trial-offer-feat-lead'>{t('trialOffer.plans.pro.speakingLead')}</span>
                    <span className='trial-offer-feat-rest'>{t('trialOffer.plans.pro.speakingRest')}</span>
                  </li>
                  <li className='trial-offer-feat'>
                    <span className='trial-offer-feat-lead'>{t('trialOffer.plans.pro.welcomeCallLead')}</span>
                    <span className='trial-offer-feat-rest'>{t('trialOffer.plans.pro.welcomeCallRest')}</span>
                  </li>
                  <li className='trial-offer-feat'>
                    <span className='trial-offer-feat-lead'>{t('trialOffer.plans.pro.reviewsLead')}</span>
                    <span className='trial-offer-feat-rest'>{t('trialOffer.plans.pro.reviewsRest')}</span>
                  </li>
                  <li className='trial-offer-feat'>
                    <span className='trial-offer-feat-lead'>{t('trialOffer.plans.pro.historyLead')}</span>
                    <span className='trial-offer-feat-rest'>{t('trialOffer.plans.pro.historyRest')}</span>
                  </li>
                </ul>
              </div>
            )}

            {standardPlan && (
              <div className='trial-offer-card trial-offer-card--standard'>
                <div className='trial-offer-card-badge'>{t('trialOffer.plans.standard.badge')}</div>
                <h3 className='trial-offer-card-name'>{t('trialOffer.plans.standard.name')}</h3>
                <p className='trial-offer-card-tagline'>{t('trialOffer.plans.standard.tagline')}</p>
                <div className='trial-offer-card-price'>
                  {standardPlan.regularMonthly !== undefined && (
                    <span className='trial-offer-card-amount-was'>
                      {formatMoney(standardPlan.regularMonthly, standardPlan.currency)}
                    </span>
                  )}
                  <span className='trial-offer-card-amount'>
                    {formatMoney(standardPlan.monthlyEquivalent, standardPlan.currency)}
                  </span>
                  <span className='trial-offer-card-period'>{t('trialOffer.plans.perMonth')}</span>
                </div>
                {standardPlan.regularMonthly !== undefined && (
                  <div className='trial-offer-card-billing-note'>
                    {t('trialOffer.plans.offerNote', {
                      months: standardPlan.offerDurationMonths,
                      price: formatMoney(standardPlan.regularMonthly, standardPlan.currency),
                    })}
                  </div>
                )}
                {standardPlan.yearlyCharge !== undefined && (
                  <div className='trial-offer-card-billing-note'>
                    {t('trialOffer.plans.yearlyNote', {
                      price: formatMoney(standardPlan.yearlyCharge, standardPlan.currency),
                    })}
                  </div>
                )}
                <ExpressCheckoutRow
                  priceId={standardPlan.priceId}
                  currency={standardPlan.currency}
                  couponCode={couponFor(standardPlan)}
                  trialDays={trialDaysFor(standardPlan)}
                  onConfirmStart={() => {
                    track('Onboarding Trial Checkout Started', {
                      planName: standardPlan.value,
                      billing,
                      trialDays: trialDaysFor(standardPlan),
                      method: 'wallet',
                    })
                    // Same hand-off as the button path: onboarding is done, drop the persisted
                    // state before the wallet confirmation navigates away.
                    localStorage.removeItem(ONBOARDING_PERSIST_STORAGE_KEY)
                  }}
                />
                <button
                  type='button'
                  className='trial-offer-cta trial-offer-cta--standard'
                  onClick={() => handle.startTrial(standardPlan)}
                  disabled={Boolean(processingPlan)}
                >
                  {processingPlan === standardPlan.value
                    ? t('trialOffer.startingTrial')
                    : t('trialOffer.plans.standard.cta', { days: STANDARD_TRIAL_DAYS })}
                </button>
                <ul className='trial-offer-feats'>
                  {standardPlan.features
                    ?.filter((f: any) => f?.is_active)
                    .map((f: any, i: number) => (
                      <li className='trial-offer-feat' key={i}>
                        <span className='trial-offer-feat-lead'>{planFeatureLabel(f.label)}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {errorMessage && <div className='trial-offer-error'>{t(errorMessage)}</div>}

        <p className='trial-offer-reassurance'>{t('trialOffer.plans.reassurance')}</p>

      </section>

      {/* ---------------------------------------------------------------
          Everything in one app — under the price, so it answers "what am I paying for?"
          --------------------------------------------------------------- */}
      <section className='trial-offer-block'>
        <AllInOne />
      </section>

      {/* ---------------------------------------------------------------
          What the free days look like, right under the price
          --------------------------------------------------------------- */}
      <section className='trial-offer-block trial-offer-block--timeline'>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker'>{t('trialOffer.timeline.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.timeline.title', { days: trialDays })}</h2>
        </div>
        <TrialTimeline days={trialDays} />
      </section>

      {/* ---------------------------------------------------------------
          Proof I — the daily correction, shown then filmed
          --------------------------------------------------------------- */}
      <section className='trial-offer-block'>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker trial-offer-kicker--red'>{t('trialOffer.corrections.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.corrections.title')}</h2>
          <p className='trial-offer-block-lead'>{t('trialOffer.corrections.lead')}</p>
          <p className='trial-offer-block-lead'>{t('trialOffer.corrections.demoLead')}</p>
        </div>

        <CorrectionDemo />

      </section>

      {/* ---------------------------------------------------------------
          Proof II — the 1-on-1 sessions (the "1-1 video" lives here)
          --------------------------------------------------------------- */}
      <section className='trial-offer-block trial-offer-block--sessions'>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker trial-offer-kicker--red'>{t('trialOffer.sessions.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.sessions.title')}</h2>
          <p className='trial-offer-block-lead'>{t('trialOffer.sessions.lead')}</p>
          <p className='trial-offer-block-note'>{t('trialOffer.sessions.note')}</p>
          {/* The one teacher-language notice on this page, next to the calls and corrections it is about. */}
          <p className='trial-offer-block-note'>{t('common:teacherLanguageNotice')}</p>
        </div>

        <div className='trial-offer-sessions-layout'>
          <AmbientVideo src='/videos/feedbacksession.mp4' caption={t('trialOffer.sessions.videoCaption')} />

          <SessionSwitcher onPick={handle.sessionPicked} />
        </div>
      </section>

      {/* ---------------------------------------------------------------
          Social proof — real reviews, sources kept visible
          --------------------------------------------------------------- */}
      <section className='trial-offer-block trial-offer-block--reviews'>
        <div className='trial-offer-reviews-head'>
          <FiveStars className='trial-offer-stars trial-offer-stars--lead' />
          <h2 className='trial-offer-reviews-title'>
            {t('trialOffer.reviews.title', { learners: formatNumber(PROOF_STATS[0].figure) })}
          </h2>
          <p className='trial-offer-reviews-sub'>{t('trialOffer.reviews.sub')}</p>
        </div>
        <ul className='trial-offer-reviews'>
          {LEARNER_REVIEWS.map((review) => (
            <li className='trial-offer-review' key={review.name}>
              <span className='trial-offer-review-mark' aria-hidden='true'>
                &rdquo;
              </span>
              <FiveStars />
              <blockquote className='trial-offer-review-quote'>&ldquo;{review.quote}&rdquo;</blockquote>
              <div className='trial-offer-review-by'>
                <span className='trial-offer-review-avatar' aria-hidden='true'>
                  {review.name.charAt(0)}
                </span>
                <span className='trial-offer-review-who'>
                  <span className='trial-offer-review-name'>{review.name}</span>
                  <span className='trial-offer-review-source'>{review.source}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------------------------------------------------------------
          The apps — deliberately after the plan cards. Above them it would
          hand a reader mid-decision a reason to leave the funnel.
          --------------------------------------------------------------- */}
      <section className='trial-offer-block trial-offer-block--apps'>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker'>{t('trialOffer.apps.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.apps.title')}</h2>
          <p className='trial-offer-block-lead'>{t('trialOffer.apps.lead')}</p>
        </div>

        <div className='trial-offer-apps'>
          <a
            className='trial-offer-app'
            href='https://apps.apple.com/app/id1565315227'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => track('Onboarding App Store Clicked', { store: 'ios' })}
          >
            <img src='/images/app_store.png' alt='' aria-hidden='true' />
            <span>
              <Trans t={t} i18nKey='trialOffer.apps.appStore' components={{ small: <small /> }} />
            </span>
          </a>
          <a
            className='trial-offer-app'
            href='https://play.google.com/store/apps/details?id=com.maayot'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => track('Onboarding App Store Clicked', { store: 'android' })}
          >
            <img src='/images/play_store.png' alt='' aria-hidden='true' />
            <span>
              <Trans t={t} i18nKey='trialOffer.apps.googlePlay' components={{ small: <small /> }} />
            </span>
          </a>
        </div>
      </section>

      {/* ---------------------------------------------------------------
          Last objections — answered after the price, before the way out
          --------------------------------------------------------------- */}
      <section className='trial-offer-block trial-offer-block--faq'>
        <div className='trial-offer-block-head'>
          <span className='trial-offer-kicker'>{t('trialOffer.faq.kicker')}</span>
          <h2 className='trial-offer-block-title'>{t('trialOffer.faq.title')}</h2>
        </div>
        <FaqAccordion
          onOpen={handle.faqOpened}
          onAction={() => {
            track('Onboarding Student Discount Requested')
            requestStudentDiscount(credentials?.email)
          }}
          days={trialDays}
        />
      </section>

      {/* The escape hatch stays genuinely last, after every objection is answered. */}
      {/* Forced-trial experiment concluded 2026-08-28: the no-card path is
          permanent — the drip sequence's Track A emails depend on it. */}
      <button type='button' className='trial-offer-skip' onClick={handle.confirmSkip}>
        {t('trialOffer.skip')}
      </button>

      {/* Sticky CTA — only while the plan cards are off screen, and never over the modal. */}
      {showStickyCta && !showDayPassModal && !showSkipConfirm && !showExitOffer && (
        <div className='trial-offer-sticky'>
          <div className='trial-offer-sticky-copy'>
            <strong>{t('trialOffer.sticky.title', proDays)}</strong>
            <span>{t('trialOffer.noChargeCancelAnyTime')}</span>
          </div>
          <button
            type='button'
            className='trial-offer-sticky-cta'
            onClick={() => handle.scrollToPlans('sticky')}
          >
            {t('trialOffer.sticky.cta')}
          </button>
        </div>
      )}

      {showSkipConfirm && (
        <div className='trial-offer-modal-backdrop' role='dialog' aria-modal='true' aria-labelledby='skip-confirm-title'>
          <div className='trial-offer-modal' ref={skipDialogRef} tabIndex={-1}>
            <h3 className='trial-offer-modal-title' id='skip-confirm-title'>
              {t('trialOffer.skipConfirm.title')}
            </h3>
            <p className='trial-offer-modal-body'>
              <Trans t={t} i18nKey='trialOffer.skipConfirm.body' components={{ strong: <strong /> }} />
            </p>
            <p className='trial-offer-modal-body'>{t('trialOffer.skipConfirm.value', proDays)}</p>
            <button
              type='button'
              className='trial-offer-cta trial-offer-cta--pro trial-offer-modal-cta'
              onClick={() => handle.dismissSkip('plans')}
            >
              {t('trialOffer.tryProFree', proDays)}
            </button>
            <button type='button' className='trial-offer-skip trial-offer-modal-skip' onClick={handle.grantDayPass}>
              {t('trialOffer.skipConfirm.decline')}
            </button>
          </div>
        </div>
      )}

      {showExitOffer && (
        <div className='trial-offer-modal-backdrop' role='dialog' aria-modal='true' aria-labelledby='exit-offer-title'>
          <div className='trial-offer-modal' ref={exitDialogRef} tabIndex={-1}>
            <h3 className='trial-offer-modal-title' id='exit-offer-title'>
              {t('trialOffer.exitOffer.title')}
              <br />
              {t('trialOffer.exitOffer.subtitle')}
            </h3>
            <p className='trial-offer-modal-body'>{t('trialOffer.exitOffer.body', proDays)}</p>
            <button
              type='button'
              className='trial-offer-cta trial-offer-cta--pro trial-offer-modal-cta'
              onClick={() => handle.closeExitOffer('trial')}
              disabled={Boolean(processingPlan)}
            >
              {processingPlan ? t('trialOffer.startingTrial') : t('trialOffer.exitOffer.cta', proDays)}
            </button>
            <button
              type='button'
              className='trial-offer-skip trial-offer-modal-skip'
              onClick={() => handle.closeExitOffer('close')}
            >
              {t('trialOffer.exitOffer.decline')}
            </button>
          </div>
        </div>
      )}

      {showDayPassModal && (
        <div className='trial-offer-modal-backdrop' role='dialog' aria-modal='true'>
          <div className='trial-offer-modal'>
            <div
              className={`trial-offer-unlock${!isClaimingDayPass && dayPassGranted ? ' is-unlocked' : ''}`}
              aria-hidden='true'
            >
              <span className='trial-offer-unlock-ring' />
              <svg viewBox='0 0 48 48' fill='none'>
                <path
                  className='trial-offer-unlock-shackle trial-offer-unlock-shackle--closed'
                  d='M15 22V15a9 9 0 0 1 18 0v7'
                  stroke='currentColor'
                  strokeWidth='3.5'
                  strokeLinecap='round'
                />
                <path
                  className='trial-offer-unlock-shackle trial-offer-unlock-shackle--open'
                  d='M15 22v-7a9 9 0 0 1 17.8-1.8'
                  stroke='currentColor'
                  strokeWidth='3.5'
                  strokeLinecap='round'
                />
                <rect className='trial-offer-unlock-body' x='10' y='22' width='28' height='20' rx='5' fill='currentColor' />
                <circle className='trial-offer-unlock-keyhole' cx='24' cy='31' r='2.6' fill='#fff' />
                <path
                  className='trial-offer-unlock-keyhole'
                  d='M24 32.5v3.5'
                  stroke='#fff'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                />
              </svg>
            </div>
            <h3 className='trial-offer-modal-title'>
              {isClaimingDayPass
                ? t('trialOffer.dayPass.titleUnlocking')
                : dayPassGranted
                ? t('trialOffer.dayPass.titleGranted')
                : t('trialOffer.dayPass.titleFallback')}
            </h3>
            {isClaimingDayPass || dayPassGranted ? (
              <>
                <p className='trial-offer-modal-body'>
                  <Trans
                    t={t}
                    i18nKey={
                      dayPassGranted
                        ? standardMonthlyDisplayPrice
                          ? 'onboarding:trialOffer.dayPass.bodyGrantedWithPrice'
                          : 'onboarding:trialOffer.dayPass.bodyGranted'
                        : standardMonthlyDisplayPrice
                        ? 'onboarding:trialOffer.dayPass.bodyUnlockingWithPrice'
                        : 'onboarding:trialOffer.dayPass.bodyUnlocking'
                    }
                    values={{ days: DAY_PASS_DAYS, price: standardMonthlyDisplayPrice }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className='trial-offer-modal-note'>{t('trialOffer.dayPass.note', { days: DAY_PASS_DAYS })}</p>
              </>
            ) : (
              <p className='trial-offer-modal-body'>{t('trialOffer.dayPass.fallbackBody')}</p>
            )}
            <button
              type='button'
              className='trial-offer-cta trial-offer-cta--pro trial-offer-modal-cta'
              onClick={handle.continueAfterDayPass}
              disabled={isClaimingDayPass}
            >
              {isClaimingDayPass ? t('trialOffer.dayPass.ctaUnlocking') : t('trialOffer.dayPass.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TrialOfferRedesigned
